import React, { useState } from "react";
import "./styles/cadastro.css";
import AppLayout from "../../layouts/AppLayout";
import useAuth from "../../hooks/useAuth";
import useActiveCondominio from "../../hooks/useActiveCondominio";
import { cargosPermitidos, podeGerenciar, nomeAmigavel } from "../../utils/cargos";
import useUsuarios from "./hooks/useUsuarios";
import UsuariosTable from "./components/UsuariosTable";
import UsuarioForms from "./components/UsuarioForms";

/**
 * Componente principal de gerenciamento de usuários
 * Orquestra hook customizado e componentes de apresentação
 */
const CadastroUsuarios = () => {
  const { user } = useAuth();
  const { activeCondominio, condominios } = useActiveCondominio();
  
  // Hook customizado com toda a lógica de API + AbortController
  const { usuarios, loading, error, fetchUsuarios, createUsuario, updateUsuario, deleteUsuario } = useUsuarios(activeCondominio);
  
  // Estados de UI
  const [message, setMessage] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userToDelete, setUserToDelete] = useState(null);
  
  // Calcular nome do condomínio ativo
  const condominioNome = condominios.find(c => String(c.id) === String(activeCondominio))?.nome || null;
  const cargosDisponiveis = cargosPermitidos(user?.cargo);
  
  const showMessage = (msg, type = "success") => {
    setMessage({ text: msg, type });
    setTimeout(() => setMessage(null), 5000);
  };
  
  const handleCreateSubmit = async (dadosUsuario) => {
    try {
      await createUsuario(dadosUsuario);
      showMessage("Usuário criado com sucesso", "success");
      setShowCreateModal(false);
    } catch (err) {
      console.error("Erro ao criar usuário:", err);
      showMessage(err.response?.data?.mensagem || "Erro ao criar usuário", "error");
    }
  };
  
  const handleEdit = (usuario) => {
    if (!podeGerenciar(user.cargo, usuario.cargo)) {
      showMessage("Você não pode editar este usuário", "error");
      return;
    }
    setEditingUser(usuario);
    setShowEditModal(true);
  };
  
  const handleEditSubmit = async (dadosUsuario) => {
    try {
      await updateUsuario(editingUser.id, dadosUsuario);
      showMessage("Usuário atualizado com sucesso", "success");
      setShowEditModal(false);
      setEditingUser(null);
    } catch (err) {
      console.error("Erro ao atualizar usuário:", err);
      showMessage(err.response?.data?.mensagem || "Erro ao atualizar usuário", "error");
    }
  };
  
  const handleDelete = (usuario) => {
    if (!podeGerenciar(user.cargo, usuario.cargo)) {
      showMessage("Você não pode excluir este usuário", "error");
      return;
    }
    setUserToDelete(usuario);
    setShowDeleteConfirm(true);
  };
  
  const handleDeleteConfirm = async () => {
    try {
      await deleteUsuario(userToDelete.id);
      showMessage("Usuário excluído com sucesso", "success");
      setShowDeleteConfirm(false);
      setUserToDelete(null);
    } catch (err) {
      console.error("Erro ao excluir usuário:", err);
      showMessage(err.response?.data?.mensagem || "Erro ao excluir usuário", "error");
    }
  };
  
  if (!activeCondominio) {
    return (
      <AppLayout titulo="Gerenciar Usuários">
        <div className="usuarios-container">
          <div className="usuarios-error">
            <h2>Nenhum condomínio selecionado</h2>
            <p>Selecione um condomínio no menu superior para gerenciar usuários.</p>
          </div>
        </div>
      </AppLayout>
    );
  }
  
  return (
    <AppLayout titulo="Gerenciar Usuários">
      <div className="usuarios-container">
        {/* Hero Section */}
        <div className="usuarios-hero">
          <div className="usuarios-hero-content">
            <h1 className="usuarios-hero-title">Gerenciamento de Usuários</h1>
            <p className="usuarios-hero-description">
              Crie e gerencie usuários com acesso ao sistema. 
              {user?.cargo === 'admin' 
                ? ' Como administrador, você pode criar qualquer tipo de usuário.' 
                : ` Como ${nomeAmigavel(user?.cargo)}, você pode criar usuários com cargo de Portaria para o condomínio ativo.`
              }
            </p>
          </div>
        </div>
        
        {/* Mensagem de status */}
        {message && (
          <div className={`usuarios-message usuarios-message-${message.type}`}>
            {message.text}
          </div>
        )}
        
        {/* Seção principal */}
        <section className="feature-section">
          <div className="feature-section__toolbar">
            <div className="feature-section__toolbar-start">
              <h2 className="feature-section__toolbar-title">Usuários do Sistema</h2>
              <span className="feature-section__toolbar-badge">
                Condomínio: {condominioNome || "Carregando..."}
              </span>
            </div>
            <div className="feature-section__toolbar-end">
              {cargosDisponiveis.length > 0 && (
                <button className="feature-section__toolbar-btn-primary" onClick={() => setShowCreateModal(true)}>
                  <span>+</span>
                  <span>Novo Usuário</span>
                </button>
              )}
              {/* Slot escalável: futuros botões aqui (export, filtro, etc.) */}
            </div>
          </div>
          
          {/* Tabela de usuários (componentizada) */}
          <div className="feature-section__body">
            <UsuariosTable 
              usuarios={usuarios}
              loading={loading}
              error={error}
              user={user}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onRetry={fetchUsuarios}
            />
          </div>
        </section>
      </div>
      
      {/* Modais de CRUD (componentizados) */}
      <UsuarioForms
        user={user}
        condominios={condominios}
        condominioNome={condominioNome}
        showCreateModal={showCreateModal}
        showEditModal={showEditModal}
        showDeleteConfirm={showDeleteConfirm}
        editingUser={editingUser}
        userToDelete={userToDelete}
        onCreateSubmit={handleCreateSubmit}
        onEditSubmit={handleEditSubmit}
        onDeleteConfirm={handleDeleteConfirm}
        onCloseCreate={() => setShowCreateModal(false)}
        onCloseEdit={() => { setShowEditModal(false); setEditingUser(null); }}
        onCloseDelete={() => { setShowDeleteConfirm(false); setUserToDelete(null); }}
      />
    </AppLayout>
  );
};

export default CadastroUsuarios;
